/**
 * PromptCard.tsx — Prompt 卡片（标题 / 分类 / 摘要 / 收藏 / 一键复制）
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Prompt } from '../data/seed';
import { categoryName } from '../data/seed';
import { isFavorite, toggleFavorite } from '../lib/storage';
import { trackEvent } from '../lib/analytics';
import { HeartIcon, CopyIcon, CheckIcon } from './Icons';

interface PromptCardProps {
  prompt: Prompt;
}

export default function PromptCard({ prompt }: PromptCardProps) {
  const [favorite, setFavorite] = useState(() => isFavorite(prompt.id));
  const [copied, setCopied] = useState(false);

  /** 一键复制完整 Prompt 到剪贴板 */
  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopied(true);
      trackEvent('copy_prompt', { id: prompt.id });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // 剪贴板 API 不可用时静默失败
    }
  }

  /** 切换收藏（localStorage） */
  function handleFavorite(): void {
    const next = toggleFavorite(prompt.id);
    setFavorite(next);
    trackEvent('toggle_favorite', { id: prompt.id, value: next });
  }

  const excerpt = prompt.content.length > 120 ? `${prompt.content.slice(0, 120)}…` : prompt.content;

  return (
    <article className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="mb-3 flex items-center justify-between">
        <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-600">
          {categoryName(prompt.category)}
        </span>
        <button
          type="button"
          onClick={handleFavorite}
          aria-label={favorite ? '取消收藏' : '收藏'}
          title={favorite ? '取消收藏' : '收藏'}
          className={favorite ? 'text-rose-500' : 'text-slate-300 transition hover:text-rose-400'}
        >
          <HeartIcon filled={favorite} />
        </button>
      </div>

      <Link
        to={`/prompt/${prompt.id}`}
        className="mb-2 text-base font-semibold text-slate-900 hover:text-indigo-600"
      >
        {prompt.title}
      </Link>

      <p className="mb-4 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-500">{excerpt}</p>

      <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <span>{prompt.author}</span>
          <span>·</span>
          <span>{prompt.likes} 赞</span>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 rounded-lg px-2 py-1 transition hover:bg-slate-50 hover:text-indigo-600"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          {copied ? '已复制' : '复制'}
        </button>
      </div>
    </article>
  );
}
